// App-level upload queue (§6, §11c). Mounted with the app shell, never inside a composer, so leaving the
// page, switching tab or switching business never unmounts or re-targets an upload. Every item stores its
// owner user, business, target and the batch idempotency key when it is created.
//
// Flow: batch reservation (one request per ≤ 200 files) → tus upload per reservation (lazy tus-js-client,
// 8 MB chunks, limited concurrency) → the server attaches on completion.
// After a reload the File objects are gone: the server's live reservations are listed as
// "Paused: reselect files to resume" (needs-reselect), with Reselect / Upload again / Remove.
import { useSyncExternalStore } from 'react';
import { api, ApiError, del, get, newKey, post, qs } from '../lib/api';
import { createBatchAnnouncer, announce } from '../lib/announce';
import { verifyPrefix } from './verify';
import { getCacheOwner } from '../lib/owner';
import { actingName, getSessionTag, isActingEnded, reshowFrozenDialog } from '../lib/impersonation';

export const CHUNK_SIZE = 8 * 1024 * 1024;

export type UploadTarget = { type: 'work_order' | 'task' | 'message' | 'delivery'; id: string };
export type UploadState =
  | 'reserving'
  | 'queued'
  | 'uploading'
  | 'paused'
  | 'needs-reselect'
  | 'checking'
  | 'done'
  | 'error'
  | 'refused';

export type UploadItem = {
  key: string;
  reservationId?: string;
  uploadUrl?: string;
  batchId: string;
  idempotencyKey: string;
  ownerUserId: string;
  businessId: string;
  target: UploadTarget;
  name: string;
  size: number;
  lastModified?: number;
  type?: string;
  file?: File;
  state: UploadState;
  offset: number;
  /** Bytes per second, smoothed. */
  speed: number;
  /** Progress while hashing a reselected file (0–1). */
  checkProgress?: number;
  error?: string;
  createdAt: number;
  /**
   * Session tag when the item was added (§2h). Sent unchanged on every tus request and retry, never
   * refreshed: an upload started under another identity gets 409 instead of landing in the new session.
   */
  tag?: string | null;
};

export type UploadLimits = { maxFileBytes: number; maxFilesPerBatch: number; concurrency: number };
export const DEFAULT_LIMITS: UploadLimits = { maxFileBytes: 500 * 1024 * 1024, maxFilesPerBatch: 200, concurrency: 3 };

type TusUpload = { start(): void; abort(shouldTerminate?: boolean): Promise<void> };

// ---------------- store ----------------
let items: UploadItem[] = [];
let limits: UploadLimits = DEFAULT_LIMITS;
const listeners = new Set<() => void>();
const handles = new Map<string, TusUpload>();
const doneListeners = new Set<(item: UploadItem) => void>();
const announcer = createBatchAnnouncer();

// Owner fence (§11c). abortAll() bumps the generation; every async step (reservation response, lazy tus
// import, paused-row listing, reselect hashing, Upload again) captures it first and does nothing once it
// changed or the signed-in account is no longer the item's owner. Old-account work can therefore never
// start a tus upload, re-add rows or repopulate the queue after logout or an account switch.
let generation = 0;
const alive = (gen: number, userId: string) => gen === generation && getCacheOwner() === userId;

function emit() {
  items = [...items];
  listeners.forEach((l) => l());
}
function update(key: string, patch: Partial<UploadItem>) {
  const i = items.findIndex((x) => x.key === key);
  if (i < 0) return;
  items[i] = { ...items[i]!, ...patch };
  emit();
}
const find = (key: string) => items.find((x) => x.key === key);

export function setUploadLimits(l: Partial<UploadLimits>) {
  limits = { ...limits, ...l };
  pump();
}
export function getUploadLimits() {
  return limits;
}

/** Called when a file finishes so screens can refetch the order/task it belongs to. */
export function onUploadDone(fn: (item: UploadItem) => void): () => void {
  doneListeners.add(fn);
  return () => doneListeners.delete(fn);
}

// ---------------- adding files ----------------
export type AddResult = { added: number; refused?: { code: string; message: string; fits?: number } };

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

export async function addFiles(owner: { userId: string; businessId: string }, target: UploadTarget, files: File[]): Promise<AddResult> {
  const gen = generation;
  if (!alive(gen, owner.userId)) return { added: 0 };
  if (isActingEnded()) {
    reshowFrozenDialog();
    return { added: 0, refused: { code: 'impersonation_ended', message: `Session as ${actingName() ?? 'the client'} ended.` } };
  }
  const tooBig = files.filter((f) => f.size > limits.maxFileBytes);
  const ok = files.filter((f) => f.size <= limits.maxFileBytes);
  if (tooBig.length) {
    announce(`${plural(tooBig.length, 'file')} too large (limit ${formatBytes(limits.maxFileBytes)})`, 'assertive');
  }
  let added = 0;
  let refused: AddResult['refused'];
  for (let i = 0; i < ok.length; i += limits.maxFilesPerBatch) {
    if (!alive(gen, owner.userId)) break;
    const chunk = ok.slice(i, i + limits.maxFilesPerBatch);
    const r = await reserveBatch(owner, target, chunk, gen);
    added += r.added;
    if (r.refused) {
      refused = r.refused;
      break;
    }
  }
  if (tooBig.length && !refused) refused = { code: 'file_too_large', message: `${plural(tooBig.length, 'file')} over ${formatBytes(limits.maxFileBytes)} skipped.` };
  return { added, refused };
}

type ReservationDto = { id: string; uploadUrl: string; name: string; size: number; offset: number; state: string; target: UploadTarget };

async function reserveBatch(owner: { userId: string; businessId: string }, target: UploadTarget, files: File[], gen: number): Promise<AddResult> {
  const batchId = newKey();
  const idempotencyKey = newKey();
  const now = Date.now();
  const batch: UploadItem[] = files.map((f, n) => ({
    key: `${batchId}:${n}`,
    batchId,
    idempotencyKey,
    ownerUserId: owner.userId,
    businessId: owner.businessId,
    target,
    name: f.name,
    size: f.size,
    lastModified: f.lastModified,
    type: f.type || undefined,
    file: f,
    tag: getSessionTag(),
    state: 'reserving',
    offset: 0,
    speed: 0,
    createdAt: now,
  }));
  items.push(...batch);
  emit();
  announcer.started(batchId, batch.length);
  try {
    const res = await post<{ batchId: string; reservations: ReservationDto[] }>(
      '/api/upload-reservations',
      {
        businessId: owner.businessId,
        target,
        batchId,
        files: files.map((f) => ({ name: f.name, size: f.size, lastModified: f.lastModified, type: f.type || null })),
      },
      { idempotencyKey },
    );
    if (!alive(gen, owner.userId)) return { added: 0 };
    res.reservations.forEach((r, n) => {
      const it = batch[n]!;
      update(it.key, { reservationId: r.id, uploadUrl: r.uploadUrl, offset: r.offset, state: 'queued', target: r.target ?? target });
    });
    pump();
    return { added: batch.length };
  } catch (e) {
    if (!alive(gen, owner.userId)) return { added: 0 };
    const err = e instanceof ApiError ? e : null;
    const body = (err?.body ?? {}) as { error?: string; fits?: number };
    const message = refusalMessage(body.error, body.fits, err?.message);
    for (const it of batch) {
      update(it.key, { state: 'refused', error: message });
      announcer.fileSettled(batchId, false);
    }
    return { added: 0, refused: { code: body.error ?? 'error', message, fits: body.fits } };
  }
}

function refusalMessage(code?: string, fits?: number, fallback?: string): string {
  const fit = fits != null ? ` ${fits === 0 ? 'No files' : plural(fits, 'file')} would fit. Try fewer.` : '';
  switch (code) {
    case 'disk_full':
      return 'Storage is full, contact your team.' + fit;
    case 'quota_exceeded':
      return 'This business has reached its storage limit.' + fit;
    case 'too_many_files':
      return `Too many files in one go (max ${limits.maxFilesPerBatch}).`;
    case 'file_too_large':
      return `A file is over the ${formatBytes(limits.maxFileBytes)} limit.`;
    case 'type_not_allowed':
      return 'This file type is not allowed.';
    default:
      return fallback ?? 'Upload could not start.';
  }
}

// ---------------- running uploads ----------------
function pump() {
  if (isActingEnded()) return; // frozen: nothing starts or resumes (§6)
  const running = items.filter((x) => x.state === 'uploading').length;
  let free = limits.concurrency - running;
  for (const it of items) {
    if (free <= 0) break;
    if (it.state === 'queued' && it.file && it.uploadUrl && it.ownerUserId === getCacheOwner()) {
      free--;
      void start(it.key);
    }
  }
}

async function start(key: string) {
  const it = find(key);
  if (!it?.file || !it.uploadUrl) return;
  const gen = generation;
  if (!alive(gen, it.ownerUserId) || isActingEnded()) return;
  update(key, { state: 'uploading', error: undefined });
  const { Upload } = await import('tus-js-client');
  // The lazy import can resolve after logout, an account switch, Remove or Pause: never start then.
  if (!alive(gen, it.ownerUserId) || find(key)?.state !== 'uploading' || handles.has(key)) return;
  let lastT = performance.now();
  let lastB = it.offset;
  let lastEmit = 0;
  const upload = new Upload(it.file, {
    uploadUrl: it.uploadUrl,
    chunkSize: CHUNK_SIZE,
    retryDelays: [0, 1000, 3000, 5000, 10000, 20000],
    storeFingerprintForResuming: false,
    removeFingerprintOnSuccess: true,
    headers: { 'X-Requested-With': 'workdesk', ...(it.tag ? { 'X-Session-Tag': it.tag } : {}) },
    metadata: { filename: it.name, filetype: it.type ?? '' },
    onProgress(sent) {
      const now = performance.now();
      if (now - lastEmit < 250 && sent < it.size) return; // throttle re-renders
      const dt = (now - lastT) / 1000;
      const cur = find(key);
      if (!cur) return;
      const inst = dt > 0 ? (sent - lastB) / dt : 0;
      lastT = now;
      lastB = sent;
      lastEmit = now;
      update(key, { offset: sent, speed: cur.speed ? cur.speed * 0.7 + inst * 0.3 : inst });
    },
    onSuccess() {
      handles.delete(key);
      update(key, { state: 'done', offset: it.size, speed: 0 });
      announcer.fileSettled(it.batchId, true);
      const done = find(key);
      if (done) doneListeners.forEach((l) => l(done));
      pump();
    },
    onError(err) {
      handles.delete(key);
      const cur = find(key);
      if (!cur || cur.state === 'paused') return;
      const status = (err as { originalResponse?: { getStatus(): number } }).originalResponse?.getStatus?.();
      const msg =
        status === 409
          ? 'Your account changed in another tab.'
          : status === 404 || status === 410
          ? 'This upload expired. Upload again.'
          : status === 403
            ? 'You no longer have access to this business.'
            : 'Upload failed. Retry when you are back online.';
      update(key, { state: 'error', error: msg, speed: 0 });
      announcer.fileSettled(it.batchId, false);
      pump();
    },
  } as ConstructorParameters<typeof Upload>[1]);
  handles.set(key, upload as unknown as TusUpload);
  upload.start();
}

export function pause(key: string) {
  const h = handles.get(key);
  handles.delete(key);
  update(key, { state: 'paused', speed: 0 });
  void h?.abort(false);
  pump();
}

export function resume(key: string) {
  const it = find(key);
  if (!it) return;
  if (isActingEnded()) {
    reshowFrozenDialog();
    return;
  }
  if (!it.file) {
    update(key, { state: 'needs-reselect' });
    return;
  }
  update(key, { state: 'queued', error: undefined });
  pump();
}

export const retry = resume;

/** Remove: abort, delete the reservation (frees reserved bytes, deletes partial data), drop the row. */
export async function remove(key: string) {
  const it = find(key);
  if (!it) return;
  const h = handles.get(key);
  handles.delete(key);
  await h?.abort(false).catch(() => undefined);
  if (it.reservationId && it.state !== 'done') {
    await del(`/api/upload-reservations/${it.reservationId}`).catch(() => undefined);
  }
  items = items.filter((x) => x.key !== key);
  if (it.state !== 'done' && it.state !== 'error' && it.state !== 'refused') announcer.removed(it.batchId);
  emit();
  pump();
}

/** Clear finished rows from the queue sheet. */
export function clearFinished() {
  items = items.filter((x) => x.state !== 'done' && x.state !== 'refused');
  emit();
}

// ---------------- after reload: paused rows ----------------
type LiveReservation = ReservationDto & { batchId: string; lastModified?: number | null; type?: string | null };

/** Lists this user's unresolved reservations for a business as "Paused: reselect files to resume". */
export async function loadPaused(owner: { userId: string; businessId: string }) {
  const gen = generation;
  if (!alive(gen, owner.userId)) return;
  let live: LiveReservation[] = [];
  try {
    live = await get<LiveReservation[]>(`/api/upload-reservations${qs({ businessId: owner.businessId, live: 1 })}`);
  } catch {
    return;
  }
  if (!alive(gen, owner.userId)) return;
  const known = new Set(items.map((x) => x.reservationId).filter(Boolean));
  let changed = false;
  for (const r of live) {
    if (known.has(r.id)) continue;
    items.push({
      key: r.id,
      reservationId: r.id,
      uploadUrl: r.uploadUrl,
      batchId: r.batchId,
      idempotencyKey: '',
      ownerUserId: owner.userId,
      businessId: owner.businessId,
      target: r.target,
      name: r.name,
      size: r.size,
      lastModified: r.lastModified ?? undefined,
      type: r.type ?? undefined,
      state: 'needs-reselect',
      offset: r.offset,
      speed: 0,
      createdAt: Date.now(),
      tag: getSessionTag(),
    });
    changed = true;
  }
  if (changed) emit();
}

/**
 * Reselect: match picked files to paused rows by size (name and lastModified are hints only, iOS pickers
 * rename to image.jpg and reset lastModified), then verify the full uploaded prefix against the stored
 * per-8 MB chunk hashes before anything is appended. Returns how many rows resumed / were rejected.
 */
export async function reselect(keys: string[], files: File[]): Promise<{ resumed: number; rejected: number; unmatched: number }> {
  const gen = generation;
  const rows = keys.map(find).filter((x): x is UploadItem => !!x && !x.file && alive(gen, x.ownerUserId));
  const pool = [...files];
  let resumed = 0;
  let rejected = 0;
  let unmatched = 0;
  for (const row of rows) {
    if (!alive(gen, row.ownerUserId)) return { resumed, rejected, unmatched };
    const candidates = pool.filter((f) => f.size === row.size);
    if (candidates.length === 0) {
      unmatched++;
      continue;
    }
    candidates.sort((a, b) => hintScore(b, row) - hintScore(a, row));
    let matched: File | null = null;
    update(row.key, { state: 'checking', checkProgress: 0, error: undefined });
    for (const f of candidates) {
      const ok = await verifyPrefix(row.reservationId!, f, (p) => update(row.key, { checkProgress: p })).catch(() => false);
      if (ok) {
        matched = f;
        break;
      }
    }
    if (!alive(gen, row.ownerUserId)) return { resumed, rejected, unmatched };
    if (matched) {
      pool.splice(pool.indexOf(matched), 1);
      update(row.key, { file: matched, state: 'queued', checkProgress: undefined });
      resumed++;
    } else {
      update(row.key, { state: 'needs-reselect', checkProgress: undefined, error: "This isn't the same file." });
      rejected++;
    }
  }
  if (resumed) announce(`${plural(resumed, 'file')} resuming`, 'polite');
  if (rejected) announce(`${plural(rejected, 'file')} didn't match. Upload again instead.`, 'assertive');
  pump();
  return { resumed, rejected, unmatched };
}

function hintScore(f: File, row: UploadItem): number {
  return (f.name === row.name ? 2 : 0) + (row.lastModified && f.lastModified === row.lastModified ? 1 : 0);
}

/** Upload again: a new reservation for a newly picked file; the old one is removed. */
export async function uploadAgain(key: string, file: File): Promise<AddResult> {
  const row = find(key);
  if (!row) return { added: 0 };
  const owner = { userId: row.ownerUserId, businessId: row.businessId };
  const target = row.target;
  const gen = generation;
  await remove(key);
  if (!alive(gen, owner.userId)) return { added: 0 };
  return addFiles(owner, target, [file]);
}

/** After a weekly merge the server retargets reservations; mirror it locally for display. */
export function retarget(fromOrderId: string, toOrderId: string) {
  let changed = false;
  items = items.map((x) => {
    if (x.target.type === 'work_order' && x.target.id === fromOrderId) {
      changed = true;
      return { ...x, target: { type: 'work_order', id: toOrderId } };
    }
    return x;
  });
  if (changed) emit();
}

/** Logout / account change: abort everything and forget it (§11c). Reservations expire server-side. */
export async function abortAll() {
  generation++;
  const hs = [...handles.values()];
  handles.clear();
  items = [];
  emit();
  await Promise.all(hs.map((h) => h.abort(false).catch(() => undefined)));
}

// ---------------- hooks ----------------
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export function useUploads(): UploadItem[] {
  return useSyncExternalStore(subscribe, () => items, () => items);
}
export function uploadsSnapshot(): UploadItem[] {
  return items;
}

export const isLive = (s: UploadState) => s === 'reserving' || s === 'queued' || s === 'uploading' || s === 'checking';
export const needsAttention = (s: UploadState) => s === 'needs-reselect' || s === 'error' || s === 'paused';

export function summarize(list: UploadItem[]) {
  const active = list.filter((x) => isLive(x.state));
  const total = active.reduce((a, x) => a + x.size, 0);
  const sent = active.reduce((a, x) => a + x.offset, 0);
  return {
    live: active.length,
    attention: list.filter((x) => needsAttention(x.state)).length,
    done: list.filter((x) => x.state === 'done').length,
    total,
    sent,
    pct: total ? Math.round((sent / total) * 100) : 0,
    speed: active.reduce((a, x) => a + (x.state === 'uploading' ? x.speed : 0), 0),
  };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const u = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
}

// test hook
export const __test = {
  reset() {
    generation++;
    items = [];
    handles.clear();
    limits = DEFAULT_LIMITS;
    emit();
  },
  api,
};
